/**
 * Audit trail — who did what, when.
 *
 * Peribott Dynamic LLP — Author: Amit Jagnani
 *
 * v6.3.48. The existing audit view was buried in User Management and only
 * showed rows for one user. This is the fleet-wide view an incident review
 * needs: filter by category (safety / data / account), robot serial, action
 * and time window.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, TextField, MenuItem, Button,
  Chip, LinearProgress, Alert, Tooltip, TablePagination,
} from '@mui/material';
import { Refresh, Warning, Storage, Person, Gamepad } from '@mui/icons-material';
import apiClient from '../../utils/apiClient';

const CATEGORIES = [
  { value: '', label: 'All activity' },
  { value: 'safety', label: 'Safety — robot movement' },
  { value: 'data', label: 'Data — deletions & backups' },
  { value: 'account', label: 'Accounts & access' },
];

const PERIODS = [
  { value: 1, label: 'Last 24 hours' },
  { value: 7, label: 'Last 7 days' },
  { value: 30, label: 'Last 30 days' },
  { value: 90, label: 'Last 90 days' },
];

// Actions worth making visually obvious in a long list.
const ACTION_STYLE: Record<string, { color: any; icon?: React.ReactNode }> = {
  emergency_stop: { color: 'error', icon: <Warning sx={{ fontSize: 14 }} /> },
  robot_shutdown: { color: 'error' },
  robot_reboot: { color: 'warning' },
  teleop_session: { color: 'info', icon: <Gamepad sx={{ fontSize: 14 }} /> },
  nav_goal: { color: 'default' },
  nav_to_waypoint: { color: 'default' },
  nav_cancelled: { color: 'default' },
  map_deleted: { color: 'error', icon: <Storage sx={{ fontSize: 14 }} /> },
  backup_restored: { color: 'error' },
  backup_deleted: { color: 'warning' },
  backup_created: { color: 'success' },
  backup_uploaded: { color: 'default' },
  password_reset_by_admin: { color: 'warning', icon: <Person sx={{ fontSize: 14 }} /> },
  password_changed: { color: 'default' },
  autodock_triggered: { color: 'warning' },
  autodock_configured: { color: 'default' },
  map_version_restored: { color: 'warning' },
  '2fa_disabled': { color: 'warning' },
};

/** Turn a details blob into one readable line. */
const summarise = (log: any): string => {
  const d = log.details || {};
  switch (log.action) {
    case 'teleop_session':
      return `${d.robot_serial ?? '?'} · ${d.duration_seconds ?? '?'}s · ${d.commands ?? '?'} commands · peak ${d.max_linear_mps ?? '?'} m/s`;
    case 'emergency_stop':
    case 'robot_shutdown':
    case 'robot_reboot':
    case 'nav_cancelled':
      return `${d.robot_serial ?? '?'}${d.delivered === false ? ' (not delivered — robot offline)' : ''}`;
    case 'nav_goal':
      return `${d.robot_serial ?? '?'} → x ${d.x}, y ${d.y}`;
    case 'nav_to_waypoint':
      return `${d.robot_serial ?? '?'} → "${d.waypoint ?? '?'}"`;
    case 'autodock_triggered':
      return `${d.robot_serial ?? '?'} at ${d.battery_pct}% (limit ${d.threshold}%) → "${d.waypoint}"`;
    case 'map_version_restored':
      return `map "${d.name ?? '?'}" rolled back to version ${d.version}`;
    case 'map_deleted':
      return `map "${d.name ?? '?'}" deleted`;
    case 'backup_restored':
      return `${d.filename ?? '?'} restored over the live database`;
    case 'backup_created':
    case 'backup_deleted':
    case 'backup_uploaded':
      return `${d.filename ?? '?'}${d.size_mb ? ` (${d.size_mb} MB)` : ''}`;
    case 'password_reset_by_admin':
      return `reset password for ${d.target_email ?? '?'}`;
    default:
      return Object.keys(d).length ? JSON.stringify(d) : '—';
  }
};

const AuditTrail: React.FC = () => {
  const [logs, setLogs] = useState<any[]>([]);
  const [users, setUsers] = useState<Record<string, string>>({});
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [category, setCategory] = useState('safety');
  const [robotSerial, setRobotSerial] = useState('');
  const [days, setDays] = useState(7);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: any = { limit: rowsPerPage, offset: page * rowsPerPage, days };
      if (category) params.category = category;
      if (robotSerial.trim()) params.robot_serial = robotSerial.trim();
      const r = await apiClient.get('/admin/audit-logs', { params });
      setLogs(r.data?.logs || []);
      setTotal(r.data?.total ?? 0);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Could not load the audit trail');
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [category, robotSerial, days, page, rowsPerPage]);

  useEffect(() => { load(); }, [load]);

  // Resolve user ids to emails so rows say who, not a UUID.
  useEffect(() => {
    apiClient.get('/admin/users', { params: { limit: 200 } })
      .then((r) => {
        const map: Record<string, string> = {};
        (r.data?.users || r.data || []).forEach((u: any) => { map[u.id] = u.email; });
        setUsers(map);
      })
      .catch(() => { /* names are a nicety; ids still render */ });
  }, []);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 220 }}>
          <Typography variant="h4" fontWeight={700}>Audit Trail</Typography>
          <Typography variant="body2" color="text.secondary">
            Who moved the robots, and who changed or deleted things
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<Refresh />} onClick={load} disabled={loading}>
          Refresh
        </Button>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField select size="small" label="Category" value={category}
            onChange={(e) => { setCategory(e.target.value); setPage(0); }}
            sx={{ minWidth: 220 }}>
            {CATEGORIES.map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
          </TextField>
          <TextField size="small" label="Robot serial" value={robotSerial}
            onChange={(e) => { setRobotSerial(e.target.value); setPage(0); }}
            placeholder="e.g. OEM2608001" sx={{ minWidth: 200 }} />
          <TextField select size="small" label="Period" value={days}
            onChange={(e) => { setDays(Number(e.target.value)); setPage(0); }}
            sx={{ minWidth: 160 }}>
            {PERIODS.map((p) => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
          </TextField>
        </CardContent>
      </Card>

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Card>
        <TableContainer component={Paper} elevation={0}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>When</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Who</TableCell>
                <TableCell>Detail</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.length === 0 && (
                <TableRow><TableCell colSpan={4} align="center" sx={{ py: 5 }}>
                  <Typography variant="body2" color="text.secondary">
                    {loading ? 'Loading…'
                      : 'Nothing recorded for these filters. Note that entries only exist from v6.3.48 onward.'}
                  </Typography>
                </TableCell></TableRow>
              )}
              {logs.map((log) => {
                const st = ACTION_STYLE[log.action] || { color: 'default' };
                return (
                  <TableRow key={log.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title={log.timestamp || ''}>
                        <Typography variant="caption">
                          {log.timestamp ? new Date(log.timestamp).toLocaleString() : '—'}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" color={st.color} icon={st.icon as any}
                        variant={st.color === 'default' ? 'outlined' : 'filled'}
                        label={log.action} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {log.user_id ? (users[log.user_id] || log.user_id.slice(0, 8)) : 'system'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {summarise(log)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[25, 50, 100]}
        />
      </Card>
    </Box>
  );
};

export default AuditTrail;
